import { useRouterHashScroll } from 'hooks/useRouterHashScroll';
import { Route, Routes } from 'react-router-dom';

import { TemporaryStorageHandler } from 'components/TemporaryStorageHandler';
import { ABExperiment } from 'containers/ABExperiment';
import ErrorBoundary from 'containers/ErrorBoundary';
import { RequireAuthentication } from 'containers/RequireAuthentication';
import SetFlashMessages from 'containers/SetFlashMessages';
import SetGMapsError from 'containers/SetGMapsError';
import { ShowUserModals } from 'containers/ShowUserModals';
import { TabContextProvider } from 'containers/TabContext';
import { UpdateRequiredContextProvider } from 'containers/UpdateRequiredContext';
import { CmsDashboardApiPage } from 'modules/cmsDashboard/api/types/CmsDashboardApiPage';
import { GradProgramPageContainerLoadable } from 'modules/gradSchools/pages/GradProgramPageContainerLoadable';
import { AdminsLinksLoadable } from 'modules/main/pages/AdminsLinks';
import { BuyCreditsLinksLoadable } from 'modules/main/pages/BuyCreditsLinks';
import { CreditsLinksLoadable } from 'modules/main/pages/CreditsLinks';
import { EditOrgLinksLoadable } from 'modules/main/pages/EditOrgLinks';
import { ForgotPasswordPageContainerLoadable } from 'modules/main/pages/ForgotPassword';
import { GradFairsLinksLoadable } from 'modules/main/pages/GradFairsLinks';
import { ImportedListingPageContainerLoadable } from 'modules/main/pages/ImportedListing';
import { IntegrateAtsLinksLoadable } from 'modules/main/pages/IntegrateAtsLinks';
import { InvalidLinkLoadable } from 'modules/main/pages/InvalidLink';
import { InvoiceViewContainerLoadable } from 'modules/main/pages/InvoiceView';
import { InvoicesLinksLoadable } from 'modules/main/pages/InvoicesLinks';
import { ListingPageContainerLoadable } from 'modules/main/pages/Listing';
import { LoginPageContainerLoadable } from 'modules/main/pages/Login';
import { NotificationsLinksLoadable } from 'modules/main/pages/NotificationsLinks';
import { OrgPageContainerLoadable } from 'modules/main/pages/Org';
import { RateRecommendedCandidateLoadable } from 'modules/main/pages/RateRecommendedCandidate';
import { ResetPasswordPageContainerLoadable } from 'modules/main/pages/ResetPassword';
import { SignupPageContainerLoadable } from 'modules/main/pages/Signup';
import { UserInviteContainerLoadable } from 'modules/main/pages/UserInvite';
import { OrgDashboardAppLoadable } from 'modules/orgDashboard/OrgDashboardAppLoadable';
import { orgDashboardRouteGroup } from 'modules/orgDashboard/orgDashboard.routes';
import { CompensationSurveyLoadable } from 'modules/salaries/pages/CompensationSurvey/CompensationSurveyLoadable';
import { SalaryReportLoadable } from 'modules/salaries/pages/SalaryReport/SalaryReportLoadable';
import { UserDashboardAppLoadable } from 'modules/userDashboard/UserDashboardAppLoadable';
import { CreateOrgPageContainerLoadable } from 'modules/userDashboard/pages/CreateOrg/CreateOrgPageContainerLoadable';
import { CreateOrgWithGradProgramPageContainerLoadable } from 'modules/userDashboard/pages/CreateOrgWithGradProgram/CreateOrgWithGradProgramPageContainerLoadable';
import { ManageGradProgramPageContainerLoadable } from 'modules/userDashboard/pages/ManageGradProgram/ManageGradProgramPageContainerLoadable';
import { UserDashboardPostAListingPageContainerLoadable } from 'modules/userDashboard/pages/PostAListing/UserDashboardPostAListingPageContainerLoadable';
import { SelectOrgDashboardLoadable } from 'modules/userDashboard/pages/SelectOrgDashboard/SelectOrgDashboardLoadable';
import { ThemeGlobalStyle } from 'theme';
import { AmpContext } from 'utils/AmpContext';
import { WithRouterParams } from 'utils/WithRouterParams';
import { configValue } from 'utils/config';
import { SIGN_UP_TO_POST_URL_FOR_LOCALE } from 'utils/constants';

import { getCmsComponent } from './helpers/getCmsComponent';

const ORG_TYPE_URL_SEGMENTS = [
  // en
  'nonprofit',
  'government',
  'consultant',
  'social-enterprise',
  'recruiter',
  'business',
  // es
  'ong',
  'gobierno',
  'consultor',
  'empresa-social',
  'agencia-de-contratacion',
  'empresa',
  // pt
  // 'ong', already in es
  'governo',
  'consultoria',
  'empreendedorismo-social',
  'recrutador',
  // 'empresa', already in es
];

const JOB_URL_SEGMENTS = [
  'job', // en
  'empleo', // es
  'emprego', // pt
];

const INTERNSHIP_URL_SEGMENTS = [
  'internship', // en
  'pasantia', // es
  'estagio', // pt
];

const VOLOP_URL_SEGMENTS = [
  'volunteer-opportunity', // en
  'oportunidad-voluntariado', // es
  'oportunidade-voluntariado', // pt
];

const GRAD_PROGRAM_URL_SEGMENTS = [
  'grad-program', // en
  'programa-posgrado', // es
  'programa-posgraduacao', // pt
];

type Props = {
  content?: CmsDashboardApiPage;
  requestIsAmp?: boolean;
};

export default function MainApp({ content, requestIsAmp }: Props) {
  useRouterHashScroll();

  return (
    <ErrorBoundary>
      <ThemeGlobalStyle />
      <SetGMapsError />
      <SetFlashMessages />
      <TemporaryStorageHandler />
      <ShowUserModals>
        <AmpContext.Provider value={Boolean(requestIsAmp)}>
          <UpdateRequiredContextProvider>
            <TabContextProvider>
              <Routes>
                <Route
                  path="/amp/*"
                  element={getCmsComponent({ content, amp: true })}
                />
                <Route path="/login" element={<LoginPageContainerLoadable />} />
                <Route
                  path="/signup"
                  element={<SignupPageContainerLoadable />}
                />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPageContainerLoadable />}
                />
                <Route
                  path="/reset-password/:token"
                  element={
                    <WithRouterParams
                      render={({ token }) => (
                        <ResetPasswordPageContainerLoadable token={token} />
                      )}
                    />
                  }
                />
                <Route
                  path="/invalid-or-expired-link"
                  element={<InvalidLinkLoadable />}
                />
                <Route
                  path="/salaries/survey"
                  element={<CompensationSurveyLoadable />}
                />
                <Route
                  path="/salaries/explore"
                  element={<SalaryReportLoadable />}
                />
                <Route
                  path="/invoices/:invoiceId"
                  element={
                    <WithRouterParams
                      render={({ invoiceId }) => (
                        <InvoiceViewContainerLoadable invoiceId={invoiceId} />
                      )}
                    />
                  }
                />
                <Route
                  path="/donation-receipts/:invoiceId"
                  element={
                    <WithRouterParams
                      render={({ invoiceId }) => (
                        <InvoiceViewContainerLoadable
                          invoiceId={invoiceId}
                          isDonation
                        />
                      )}
                    />
                  }
                />
                {ORG_TYPE_URL_SEGMENTS.map((orgTypeSegment) => (
                  <Route
                    key={`view-org-amp-${orgTypeSegment}`}
                    path={`/:lang/amp/${orgTypeSegment}/:orgIdDashSlug`}
                    element={
                      <WithRouterParams
                        render={({ orgIdDashSlug }) => (
                          <OrgPageContainerLoadable
                            orgId={orgIdDashSlug.split('-')[0]}
                          />
                        )}
                      />
                    }
                  />
                ))}
                {ORG_TYPE_URL_SEGMENTS.map((orgTypeSegment) => (
                  <Route
                    key={`view-org-${orgTypeSegment}`}
                    path={`/:lang/${orgTypeSegment}/:orgIdDashSlug`}
                    element={
                      <WithRouterParams
                        render={({ orgIdDashSlug }) => (
                          <OrgPageContainerLoadable
                            orgId={orgIdDashSlug.split('-')[0]}
                          />
                        )}
                      />
                    }
                  />
                ))}
                {JOB_URL_SEGMENTS.map((jobUrlSegment) => (
                  <Route
                    key={`view-job-amp-${jobUrlSegment}`}
                    path={`/:lang/amp/${jobUrlSegment}/:jobIdDashSlug`}
                    element={
                      <WithRouterParams
                        render={({ jobIdDashSlug }) => (
                          <ListingPageContainerLoadable
                            listingType="JOB"
                            listingId={jobIdDashSlug.split('-')[0]}
                          />
                        )}
                      />
                    }
                  />
                ))}
                {JOB_URL_SEGMENTS.map((jobUrlSegment) => (
                  <Route
                    key={`view-job-${jobUrlSegment}`}
                    path={`/:lang/${jobUrlSegment}/:jobIdDashSlug`}
                    element={
                      <WithRouterParams
                        render={({ jobIdDashSlug }) => (
                          <ListingPageContainerLoadable
                            listingType="JOB"
                            listingId={jobIdDashSlug.split('-')[0]}
                          />
                        )}
                      />
                    }
                  />
                ))}
                {ORG_TYPE_URL_SEGMENTS.map((orgTypeSegment) =>
                  JOB_URL_SEGMENTS.map((jobUrlSegment) => (
                    <Route
                      key={`view-org-job-amp-${orgTypeSegment}-${jobUrlSegment}`}
                      path={`/:lang/amp/${orgTypeSegment}-${jobUrlSegment}/:jobIdDashSlug`}
                      element={
                        <WithRouterParams
                          render={({ jobIdDashSlug }) => (
                            <ListingPageContainerLoadable
                              listingType="JOB"
                              listingId={jobIdDashSlug.split('-')[0]}
                            />
                          )}
                        />
                      }
                    />
                  ))
                )}
                {ORG_TYPE_URL_SEGMENTS.map((orgTypeSegment) =>
                  JOB_URL_SEGMENTS.map((jobUrlSegment) => (
                    <Route
                      key={`view-org-job-${orgTypeSegment}-${jobUrlSegment}`}
                      path={`/:lang/${orgTypeSegment}-${jobUrlSegment}/:jobIdDashSlug`}
                      element={
                        <WithRouterParams
                          render={({ jobIdDashSlug }) => (
                            <ListingPageContainerLoadable
                              listingType="JOB"
                              listingId={jobIdDashSlug.split('-')[0]}
                            />
                          )}
                        />
                      }
                    />
                  ))
                )}
                {INTERNSHIP_URL_SEGMENTS.map((internshipUrlSegment) => (
                  <Route
                    key={`view-internship-amp-${internshipUrlSegment}`}
                    path={`/:lang/amp/${internshipUrlSegment}/:internshipIdDashSlug`}
                    element={
                      <WithRouterParams
                        render={({ internshipIdDashSlug }) => (
                          <ListingPageContainerLoadable
                            listingType="INTERNSHIP"
                            listingId={internshipIdDashSlug.split('-')[0]}
                          />
                        )}
                      />
                    }
                  />
                ))}
                {INTERNSHIP_URL_SEGMENTS.map((internshipUrlSegment) => (
                  <Route
                    key={`view-internship-${internshipUrlSegment}`}
                    path={`/:lang/${internshipUrlSegment}/:internshipIdDashSlug`}
                    element={
                      <WithRouterParams
                        render={({ internshipIdDashSlug }) => (
                          <ListingPageContainerLoadable
                            listingType="INTERNSHIP"
                            listingId={internshipIdDashSlug.split('-')[0]}
                          />
                        )}
                      />
                    }
                  />
                ))}
                {ORG_TYPE_URL_SEGMENTS.map((orgTypeSegment) =>
                  INTERNSHIP_URL_SEGMENTS.map((internshipUrlSegment) => (
                    <Route
                      key={`view-org-internship-amp-${orgTypeSegment}-${internshipUrlSegment}`}
                      path={`/:lang/amp/${orgTypeSegment}-${internshipUrlSegment}/:internshipIdDashSlug`}
                      element={
                        <WithRouterParams
                          render={({ internshipIdDashSlug }) => (
                            <ListingPageContainerLoadable
                              listingType="INTERNSHIP"
                              listingId={internshipIdDashSlug.split('-')[0]}
                            />
                          )}
                        />
                      }
                    />
                  ))
                )}
                {ORG_TYPE_URL_SEGMENTS.map((orgTypeSegment) =>
                  INTERNSHIP_URL_SEGMENTS.map((internshipUrlSegment) => (
                    <Route
                      key={`view-org-internship-${orgTypeSegment}-${internshipUrlSegment}`}
                      path={`/:lang/${orgTypeSegment}-${internshipUrlSegment}/:internshipIdDashSlug`}
                      element={
                        <WithRouterParams
                          render={({ internshipIdDashSlug }) => (
                            <ListingPageContainerLoadable
                              listingType="INTERNSHIP"
                              listingId={internshipIdDashSlug.split('-')[0]}
                            />
                          )}
                        />
                      }
                    />
                  ))
                )}
                {VOLOP_URL_SEGMENTS.map((volopUrlSegment) => (
                  <Route
                    key={`view-volop-amp-${volopUrlSegment}`}
                    path={`/:lang/amp/${volopUrlSegment}/:volopIdDashSlug`}
                    element={
                      <WithRouterParams
                        render={({ volopIdDashSlug }) => (
                          <ListingPageContainerLoadable
                            listingType="VOLOP"
                            listingId={volopIdDashSlug.split('-')[0]}
                          />
                        )}
                      />
                    }
                  />
                ))}
                {VOLOP_URL_SEGMENTS.map((volopUrlSegment) => (
                  <Route
                    key={`view-volop-${volopUrlSegment}`}
                    path={`/:lang/${volopUrlSegment}/:volopIdDashSlug`}
                    element={
                      <WithRouterParams
                        render={({ volopIdDashSlug }) => (
                          <ListingPageContainerLoadable
                            listingType="VOLOP"
                            listingId={volopIdDashSlug.split('-')[0]}
                          />
                        )}
                      />
                    }
                  />
                ))}
                <Route
                  path="/:lang/imported-action-opps/:source/:volopId"
                  element={
                    <WithRouterParams
                      render={({ volopId, source }) => (
                        <ImportedListingPageContainerLoadable
                          // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'ImportedL... Remove this comment to see the full error message
                          source={source}
                          listingId={volopId}
                        />
                      )}
                    />
                  }
                />
                <Route
                  path="/user-invites/:inviteId"
                  element={
                    <WithRouterParams
                      render={({ inviteId }) => (
                        <UserInviteContainerLoadable inviteId={inviteId} />
                      )}
                    />
                  }
                />
                <Route
                  path="/dashboard/*"
                  element={
                    <RequireAuthentication>
                      <UserDashboardAppLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/organizations/create"
                  element={
                    <RequireAuthentication
                      loginPathname={
                        SIGN_UP_TO_POST_URL_FOR_LOCALE[CURRENT_LOCALE]
                      }
                    >
                      <CreateOrgPageContainerLoadable />
                    </RequireAuthentication>
                  }
                />
                {configValue('idealist', 'gsdEnabled') === 'true' && (
                  <Route
                    path="/organizations/create-with-grad-program"
                    element={
                      <RequireAuthentication>
                        <CreateOrgWithGradProgramPageContainerLoadable />
                      </RequireAuthentication>
                    }
                  />
                )}
                <Route
                  path={orgDashboardRouteGroup.wrapperPath}
                  element={
                    <RequireAuthentication>
                      <WithRouterParams
                        render={({ orgId }) => (
                          <OrgDashboardAppLoadable orgId={orgId} />
                        )}
                      />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/post-a-listing"
                  element={
                    <ABExperiment
                      experimentId="POST_A_LISTING_REDIRECT"
                      defaultBucket="SIGN_UP_TO_POST"
                      initializeWithDefault
                      render={(bucket) => (
                        <RequireAuthentication
                          loginPathname={
                            bucket === 'SIGN_UP_TO_POST'
                              ? SIGN_UP_TO_POST_URL_FOR_LOCALE[CURRENT_LOCALE]
                              : getText('/organizations')
                          }
                        >
                          <UserDashboardPostAListingPageContainerLoadable />
                        </RequireAuthentication>
                      )}
                    />
                  }
                />
                <Route
                  path="/select-org"
                  element={
                    <RequireAuthentication>
                      <SelectOrgDashboardLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/manage-grad-program"
                  element={
                    <RequireAuthentication>
                      <ManageGradProgramPageContainerLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/buy-credits"
                  element={
                    <RequireAuthentication>
                      <BuyCreditsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/integrate-ats"
                  element={
                    <RequireAuthentication>
                      <IntegrateAtsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <RequireAuthentication>
                      <NotificationsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/credits"
                  element={
                    <RequireAuthentication>
                      <CreditsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/admins"
                  element={
                    <RequireAuthentication>
                      <AdminsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/edit-org"
                  element={
                    <RequireAuthentication>
                      <EditOrgLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/gradfairs"
                  element={
                    <RequireAuthentication>
                      <GradFairsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/invoices"
                  element={
                    <RequireAuthentication>
                      <InvoicesLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/email/rate-recommended-candidate/:applicantId/good"
                  element={
                    <RequireAuthentication>
                      <WithRouterParams
                        render={({ applicantId }) => (
                          <RateRecommendedCandidateLoadable
                            applicantId={applicantId}
                            isGood
                          />
                        )}
                      />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/email/rate-recommended-candidate/:applicantId/bad"
                  element={
                    <RequireAuthentication>
                      <WithRouterParams
                        render={({ applicantId }) => (
                          <RateRecommendedCandidateLoadable
                            applicantId={applicantId}
                            isGood={false}
                          />
                        )}
                      />
                    </RequireAuthentication>
                  }
                />
                {GRAD_PROGRAM_URL_SEGMENTS.map((gradProgramUrlSegment) => (
                  <Route
                    key={`view-grad-program-amp-${gradProgramUrlSegment}`}
                    path={`/:lang/amp/${gradProgramUrlSegment}/:programIdDashSlug`}
                    element={
                      <WithRouterParams
                        render={({ programIdDashSlug }) => (
                          <GradProgramPageContainerLoadable
                            programId={programIdDashSlug.split('-')[0]}
                          />
                        )}
                      />
                    }
                  />
                ))}
                {GRAD_PROGRAM_URL_SEGMENTS.map((gradProgramUrlSegment) => (
                  <Route
                    key={`view-grad-program-${gradProgramUrlSegment}`}
                    path={`/:lang/${gradProgramUrlSegment}/:programIdDashSlug`}
                    element={
                      <WithRouterParams
                        render={({ programIdDashSlug }) => (
                          <GradProgramPageContainerLoadable
                            programId={programIdDashSlug.split('-')[0]}
                          />
                        )}
                      />
                    }
                  />
                ))}

                <Route
                  path="/*"
                  element={getCmsComponent({ content, amp: false })}
                />
              </Routes>
            </TabContextProvider>
          </UpdateRequiredContextProvider>
        </AmpContext.Provider>
      </ShowUserModals>
    </ErrorBoundary>
  );
}
